import { JsonLdResult } from 'vet-bones/utils';

import {
  ConnectionAction,
  ConnectionActionType,
} from 'src/common/actions/connection/connectionActionCreators';
import {
  StoredQueriesAction,
  StoredQueriesActionType,
} from 'src/common/actions/storedQueries/storedQueriesActionCreators';
import { LanguageId, graphqlRegex } from 'src/common/constants/LanguageId';
import {
  StoredQuery,
  StoredQueryState,
} from 'src/common/store/storedQueries/StoredQueriesState';
import {
  parseStoredQueriesResults,
  parseStoredQueryResult,
} from 'src/common/utils/jsonld/parseStoredQueryResults';
import { getStoredQueryPathName } from 'src/common/utils/storedQueries/getStoredQueryPathName';

const initialState = new StoredQueryState();

export const storedQueriesReducer = (
  prevState = initialState,
  action: StoredQueriesAction | ConnectionAction
): StoredQueryState => {
  switch (action.type) {
    // Reset stored queries state if a new connection is set
    case ConnectionActionType.SET_CONNECTION_SUCCESS:
      return initialState;

    case StoredQueriesActionType.GET_STORED_QUERY_SUCCESS: {
      const { isGraphQLStoredQueriesSupported } = action.payload.action;
      const jsonLdResult = action.payload.response?.body?.[0] || [];
      const parsedQuery = parseStoredQueryResult(jsonLdResult);
      const newStoredQuery = isGraphQLStoredQueriesSupported
        ? {
            ...parsedQuery,
            languageId: parsedQuery.query.match(graphqlRegex)
              ? LanguageId.GRAPHQL
              : LanguageId.SPARQL,
          }
        : parsedQuery;

      const newStoredQueryPathName = getStoredQueryPathName({
        name: newStoredQuery.name,
        path: newStoredQuery.path,
      });
      const updatedQueries = prevState.queries.map((query) => {
        return getStoredQueryPathName({
          name: query.name,
          path: query.path,
        }) === newStoredQueryPathName
          ? newStoredQuery
          : query;
      });

      return { ...prevState, queries: updatedQueries };
    }

    case StoredQueriesActionType.GET_STORED_QUERIES_SUCCESS: {
      const results: JsonLdResult[] = action.payload?.response?.body || [];
      const storedQueriesByName = parseStoredQueriesResults(results);
      const queries = Object.values(storedQueriesByName);

      const { isGraphQLStoredQueriesSupported } = action.payload?.action || {};
      if (!isGraphQLStoredQueriesSupported) {
        return {
          ...prevState,
          queries,
          isCached: true,
        };
      }

      const queriesWithLanguageId = queries.map((query) => {
        return {
          ...query,
          languageId: query.query.match(graphqlRegex)
            ? LanguageId.GRAPHQL
            : LanguageId.SPARQL,
        };
      });

      return {
        ...prevState,
        queries: queriesWithLanguageId,
        isCached: true,
      };
    }
    case StoredQueriesActionType.DELETE_STORED_QUERY_SUCCESS: {
      const deletedPathName = getStoredQueryPathName({
        name: action.payload.action.name,
        path: action.payload.action.path,
      });
      const queries = prevState.queries.filter(
        (query) =>
          getStoredQueryPathName({ name: query.name, path: query.path }) !==
          deletedPathName
      );
      const newState = {
        ...prevState,
        queries,
      };
      return newState;
    }
    case StoredQueriesActionType.MOVE_QUERY_SUCCESS: {
      const { newPath, storedQueryOptions } = action.payload.action;
      const oldPathName = getStoredQueryPathName({
        name: storedQueryOptions.name,
        path: storedQueryOptions.path,
      });
      const prevQuery = prevState.queries.find(
        (query) =>
          getStoredQueryPathName({ name: query.name, path: query.path }) ===
          oldPathName
      );
      const updatedQuery: StoredQuery = { ...prevQuery, path: newPath };
      const updatedQueries = [
        updatedQuery,
        ...prevState.queries.filter(
          (query) =>
            getStoredQueryPathName({ name: query.name, path: query.path }) !==
            oldPathName
        ),
      ];
      return { ...prevState, queries: updatedQueries };
    }
    case StoredQueriesActionType.STORE_QUERY_SUCCESS: {
      const { storeQueryOptions } = action.payload.action;
      const newQuery = storeQueryOptions as any;
      return {
        ...prevState,
        queries: [...prevState.queries, newQuery],
      };
    }
    case StoredQueriesActionType.REMOVE_UNAVAILABLE_STORED_QUERY: {
      const { queryName, queryPath } = action.payload;
      const targetPathName = getStoredQueryPathName({
        name: queryName,
        path: queryPath,
      });
      return {
        ...prevState,
        queries: prevState.queries.filter(
          (query) =>
            getStoredQueryPathName({ name: query.name, path: query.path }) !==
            targetPathName
        ),
      };
    }
    case StoredQueriesActionType.RENAME_QUERY_SUCCESS: {
      const { oldName, oldPath, newName } = action.payload.action;
      const oldPathName = getStoredQueryPathName({
        name: oldName,
        path: oldPath,
      });
      const updatedQueries = prevState.queries.map((storedQuery) => ({
        ...storedQuery,
        name:
          getStoredQueryPathName({
            name: storedQuery.name,
            path: storedQuery.path,
          }) === oldPathName
            ? newName
            : storedQuery.name,
      }));
      return { ...prevState, queries: updatedQueries };
    }
    case StoredQueriesActionType.UPDATE_QUERY_SUCCESS: {
      const { storedQueryOptions } = action.payload.action;
      // changes to notebook state are handled in that reducer, this should only
      // update the stored queries state
      const updatedPathName = getStoredQueryPathName({
        name: storedQueryOptions.name,
        path: storedQueryOptions.path,
      });
      const prevQuery = prevState.queries.find(
        (query) =>
          getStoredQueryPathName({ name: query.name, path: query.path }) ===
          updatedPathName
      );
      const updatedQuery: StoredQuery = { ...prevQuery, ...storedQueryOptions };
      const updatedQueries = [
        updatedQuery,
        ...prevState.queries.filter(
          (query) =>
            getStoredQueryPathName({ name: query.name, path: query.path }) !==
            updatedPathName
        ),
      ];
      return { ...prevState, queries: updatedQueries };
    }
    default:
      return prevState;
  }
};
